/** Longest side kept for screenshots: sharp on a 1440p screen, small to store and load. */
export const MAX_SIDE = 2560;
/** Files under this size are sent as they are. */
const SMALL_ENOUGH = 1.5 * 1024 * 1024;

/**
 * Makes a big screenshot small before upload: at most MAX_SIDE pixels on its
 * longest side, re-encoded as WebP (JPEG where the browser cannot write WebP).
 * A 5 MB 1440p PNG usually becomes a few hundred KB. Returns the original file
 * when it is already small, when the browser cannot decode it here, or when
 * re-encoding would not make it smaller.
 */
export async function shrinkImage(file: File): Promise<File> {
  if (file.size <= SMALL_ENOUGH) return file;
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // Not an image the browser can read: the server decides.
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const encode = (type: string, quality: number) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  let blob = await encode("image/webp", 0.85);
  // Browsers that cannot write WebP silently return PNG: use JPEG instead.
  if (!blob || blob.type !== "image/webp") blob = await encode("image/jpeg", 0.88);
  if (!blob || blob.size >= file.size) return file;

  const ext = blob.type === "image/webp" ? "webp" : "jpg";
  const name = file.name.replace(/\.[^.]+$/, "") + "." + ext;
  return new File([blob], name, { type: blob.type, lastModified: file.lastModified });
}
